import React, { createContext, useState, useContext, useEffect } from 'react';
import { createCheckout } from '../lib/shopify-storefront';
import {
  initMetaClickId,
  buildCheckoutTrackingAttributes,
  newEventId,
  trackAddToCart,
  trackInitiateCheckout,
} from '../lib/metaTracking';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [forceRefreshKey, setForceRefreshKey] = useState(0);

  // Shopify carts are rebuilt from the current in-memory cart to avoid stale checkout lines.
  useEffect(() => {
    localStorage.removeItem('berleto_checkout_id');
    // Capture the Meta ad click id (_fbc) so purchases can be attributed server-side.
    initMetaClickId();
  }, []);

  const addToCart = (product, quantity = 1, variantId = null) => {
    setCartItems(prev => {
      // Create unique ID for subscription vs one-time purchase
      const itemId = product.isSubscription 
        ? `${product.id}_subscription_${product.sellingPlanId}`
        : product.id;
      
      const existing = prev.find(item => item.uniqueId === itemId);
      if (existing) {
        return prev.map(item =>
          item.uniqueId === itemId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      // Ensure all required IDs are present
      const cartItem = { 
        ...product, 
        quantity, 
        variantId: variantId || product.variantId, // Use argument if provided, otherwise use product's variantId
        sellingPlanId: product.sellingPlanId, // Ensure sellingPlanId is preserved
        uniqueId: itemId,
        displayName: product.isSubscription 
          ? `${product.title || product.name} (Subscription)`
          : product.title || product.name
      };
      
      console.log('📝 Adding item to cart:', {
        id: cartItem.id,
        variantId: cartItem.variantId,
        sellingPlanId: cartItem.sellingPlanId,
        isSubscription: cartItem.isSubscription,
        price: cartItem.price
      });
      
      return [...prev, cartItem];
    });
    setIsCartOpen(true);

    // Meta Pixel: AddToCart (browser event, feeds optimization + retargeting).
    try {
      trackAddToCart({
        id: product.id,
        name: product.title || product.name,
        price: product.price,
        quantity,
      });
    } catch (e) {
      /* tracking must never block the cart */
    }
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity <= 0) {
      // Don't automatically remove - just set to minimum 1
      setCartItems(prev =>
        prev.map(item =>
          item.uniqueId === id || item.id === id ? { ...item, quantity: 1 } : item
        )
      );
    } else {
      setCartItems(prev =>
        prev.map(item =>
          item.uniqueId === id || item.id === id ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  const removeItem = (id) => {
    setCartItems(prev => prev.filter(item => item.uniqueId !== id && item.id !== id));
  };

  const clearCart = () => {
    // Close cart first to prevent UI issues
    setIsCartOpen(false);
    
    // Clear all cart-related state
    setCartItems([]);
    setCheckoutUrl(null);
    setIsCreatingCheckout(false);
    
    // Clear localStorage
    localStorage.removeItem('berleto_checkout_id');
    
    // Force complete re-render
    setForceRefreshKey(prev => prev + 1);
    
    // Force a small delay to ensure state is updated
    setTimeout(() => {
      console.log('Cart cleared successfully');
    }, 100);
  };

  const forceRefresh = () => {
    setForceRefreshKey(prev => prev + 1);
  };

  const cartItemsCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Create Shopify checkout
  const initiateCheckout = async () => {
    if (cartItems.length === 0) return;
    
    setIsCreatingCheckout(true);
    
    try {
      console.log('🛒 Cart items before checkout:', cartItems);
      
      // Prepare line items for Shopify Cart API
      const lineItems = cartItems
        .filter(item => {
          const hasVariantId = !!item.variantId;
          if (!hasVariantId) {
            console.warn('⚠️ Item missing variantId:', item);
          }
          return hasVariantId;
        })
        .map(item => {
          const lineItem = {
            merchandiseId: item.variantId, // Cart API uses merchandiseId instead of variantId
            quantity: item.quantity
          };
          
          // Add sellingPlanId if it's a subscription
          if (item.sellingPlanId) {
            lineItem.sellingPlanId = item.sellingPlanId;
            console.log('📦 Adding subscription item:', {
              variantId: item.variantId,
              sellingPlanId: item.sellingPlanId
            });
          }
          
          return lineItem;
        });

      console.log('📦 Line items for Shopify checkout:', lineItems);

      if (lineItems.length === 0) {
        console.error('❌ No valid products for checkout. Cart items:', cartItems);
        throw new Error('No valid products for checkout');
      }

      // Meta tracking: generate a shared event id, fire InitiateCheckout, and attach
      // _fbp/_fbc/_fb_event_id to the cart so the order webhook can send Purchase (CAPI).
      const fbEventId = newEventId();
      const trackingAttributes = buildCheckoutTrackingAttributes(fbEventId);
      try {
        const checkoutValue = cartItems.reduce(
          (sum, item) => sum + (Number(item.price) || 0) * item.quantity,
          0
        );
        trackInitiateCheckout(
          {
            value: checkoutValue,
            numItems: cartItemsCount,
            contentIds: cartItems.map((item) => String(item.id)),
          },
          fbEventId
        );
      } catch (e) {
        /* tracking must never block checkout */
      }

      const newCheckout = await createCheckout(lineItems, trackingAttributes);
      setCheckoutUrl(newCheckout.checkoutUrl);

      // Add return URL to prevent navigation to Shopify theme
      const returnUrl = window.location.origin;
      const checkoutUrlWithReturn = `${newCheckout.checkoutUrl}&return_url=${encodeURIComponent(returnUrl)}`;
      window.location.href = checkoutUrlWithReturn;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Error creating checkout. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  return (
    <CartContext.Provider value={{
      isCartOpen,
      setIsCartOpen,
      cartItems,
      addToCart,
      updateQuantity,
      removeItem,
      clearCart,
      forceRefresh,
      forceRefreshKey,
      cartItemsCount,
      initiateCheckout,
      isCreatingCheckout,
      checkoutUrl
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
